import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { Flex, SearchStep, StepCarousel, Text } from '../../components'
import {
  addAnswer,
  clearNext,
  initScenario,
  selectCurrentAnswers,
  selectCurrentStep,
  selectHasFilteredPhones,
  selectIsLastStep,
  selectScenario,
  selectScenarioIsLoading,
  useDispatch,
  useSelector,
} from '../../state'
import { AllSteps, AnswerId, QuestionGroup, ScenarioType, Steps } from '../../state/interfaces'
import { FilteredPhoneList } from '../../components/phone-list'
import { Loading } from '../../components/spinners/loading'
import { useTranslation, Trans } from 'react-i18next'
import { Accordions } from '../../components/accordion/accordion'
import { Box, Divider, Show, Stack } from '@chakra-ui/react'
import { TimeIcon } from '@chakra-ui/icons'

export const FindPlanFormSteps: FC = () => {
  const dispatch = useDispatch()
  const currentStep = useSelector(selectCurrentStep)
  const currentStepAnswers = useSelector(selectCurrentAnswers)
  const scenario = useSelector(selectScenario)
  const hasFilteredPhones = useSelector(selectHasFilteredPhones)
  const scenarioIsLoading = useSelector(selectScenarioIsLoading)
  const { t } = useTranslation()
  const dispatchAddAnswer = useCallback(
    (id: AllSteps, answerIds: Array<AnswerId>) =>
      dispatch(
        addAnswer({
          stepId: id,
          answerIds,
        }),
      ),
    [dispatch],
  )
  const isLastStep = useSelector(selectIsLastStep)

  const shouldDisplayCarousel = useMemo(() => {
    let shouldDisplay = scenario && currentStep && !scenarioIsLoading

    if (currentStep?.group === QuestionGroup.PHONE_SPECS && !hasFilteredPhones) {
      shouldDisplay = false
    }

    return shouldDisplay
  }, [scenario, currentStep, scenarioIsLoading, hasFilteredPhones])

  useEffect(() => {
    dispatch(initScenario())
  }, [dispatch])

  return (
    <Flex flexDirection="column" alignItems="center">
      {scenarioIsLoading && <Loading color="black" />}
      {scenario &&
        scenario.type === ScenarioType.ADVISE_INTERNET &&
        currentStep &&
        currentStep.id === Steps.HOW_MANY_PEOPLE_SIMULTANEOUSLY && (
          <Flex flex="1" flexDirection="column" alignItems="center" mb={8}>
            <Flex mb={{ base: 4, md: 4 }}>
              <Text fontWeight={'bold'} color={'green.500'} fontSize={{ base: 20, md: 24 }}>
                {t('what-internet-do-i-need')}
              </Text>
            </Flex>
            <Show breakpoint="(min-width: 400px)">
              <Box mb={4}>
                <Trans>{t('answer-quick-questions')}</Trans>
              </Box>
            </Show>
            <Show breakpoint="(max-width: 399px)">
              <Box mb={4}>
                <Trans>{t('answer-quick-questions')}</Trans>
              </Box>
            </Show>
            <Stack mb={{ base: 4, md: 8 }} direction="row" spacing={2} alignItems="center">
              <Box as={TimeIcon} />
              <Box>
                <Text>{t('Result-less-one-minute')}</Text>
              </Box>
            </Stack>
            <Divider w={270} color={'darkGrey'} orientation="horizontal" mb={{ base: 2, md: 6 }} />
          </Flex>
        )}
      {shouldDisplayCarousel && scenario && currentStep && (
        <StepCarousel
          current={currentStep.id}
          steps={scenario.steps.map(({ answerPlaceholder, answers, question, type, id, tip, metadata }) => ({
            id,
            step: (
              <SearchStep
                key={`search-step-${id}`}
                stepId={id}
                question={question}
                questionType={type}
                answerPlaceholder={answerPlaceholder}
                answers={answers}
                currentAnswers={currentStepAnswers}
                clearNext={() => {
                  dispatch(clearNext())
                }}
                onChange={(answerIds) => dispatchAddAnswer(id, answerIds)}
                subtext={tip}
                header={
                  isLastStep
                    ? scenario.type !== ScenarioType.INTERNET
                      ? t('common_complete')
                      : undefined
                    : scenario.type === ScenarioType.MOBILE_REVIEWS ||
                      scenario.type === ScenarioType.MOBILE_REVIEWS_AWARD
                    ? t('common_mobile-reviews')
                    : scenario.type === ScenarioType.INTERNET_REVIEWS ||
                      scenario.type === ScenarioType.INTERNET_REVIEWS_AWARD
                    ? t('common_internet-reviews')
                    : scenario.type === ScenarioType.MOBILE_ALERT
                    ? t('common_mobile-alert')
                    : scenario.type === ScenarioType.INTERNET_ALERT
                    ? t('common_internet-alert')
                    : scenario.type === ScenarioType.LEAD_MOBILE ||
                      scenario.type === ScenarioType.LEAD_INTERNET ||
                      scenario.type === ScenarioType.CHECK_AVAILABILITY
                    ? 'Internet'
                    : scenario.type
                }
                metadata={metadata}
              />
            ),
          }))}
        />
      )}

      {scenario &&
        (scenario.type === ScenarioType.MOBILE_REVIEWS_AWARD ||
          scenario.type === ScenarioType.INTERNET_REVIEWS_AWARD) && (
          <Box marginY={4} paddingX={{ base: '16px', md: '0' }}>
            <Box
              maxWidth="450px"
              backgroundColor="#ECFFDC"
              margin="0 auto"
              padding="16px"
              borderRadius="8px"
              fontSize="13px"
            >
              <Text textAlign="center" fontWeight="bold" fontSize="14" marginBottom="8px" color="darkGrey">
                {t('Review-award-title')}
              </Text>
              <Text textAlign="center" color="darkGrey">
                <span>{t('review-award-text-fragment-1')}</span>
                <span style={{ fontWeight: 'bolder' }}>{t('review-award-text-fragment-2')}</span>
                <span>{t('review-award-text-fragment-3')}</span>
                <span style={{ fontWeight: 'bolder' }}>{t('review-award-text-fragment-4')}</span>
                <span>{t('review-award-text-fragment-5')}</span>
              </Text>
            </Box>
          </Box>
        )}

      {currentStep?.group === QuestionGroup.PHONE_SPECS && <FilteredPhoneList />}
      {scenario &&
        (scenario.type === ScenarioType.MOBILE_REVIEWS ||
          scenario.type === ScenarioType.INTERNET_REVIEWS ||
          scenario.type === ScenarioType.MOBILE_REVIEWS_AWARD ||
          scenario.type === ScenarioType.INTERNET_REVIEWS_AWARD ||
          scenario.type === ScenarioType.CHECK_AVAILABILITY ||
          scenario.type === ScenarioType.INTERNET_ALERT ||
          (scenario.type === ScenarioType.INTERNET &&
            currentStep &&
            currentStep.id === Steps.VALUE_CUSTOMER_OPINIONS) ||
          (scenario.type === ScenarioType.INTERNET && currentStep && currentStep.id === Steps.VALUE_COST_EFFICIENCY) ||
          (scenario.type === ScenarioType.INTERNET && currentStep && currentStep.id === Steps.VALUE_PRICE) ||
          (scenario.type === ScenarioType.MOBILE && currentStep && currentStep.id === Steps.BIG_ENTREPRISE) ||
          (scenario.type === ScenarioType.MOBILE && currentStep && currentStep.id === Steps.BIG_ENTREPRISE2) ||
          (scenario.type === ScenarioType.MOBILE && currentStep && currentStep.id === Steps.CALLING_OPTIONS) ||
          (scenario.type === ScenarioType.INTERNET &&
            currentStep &&
            (currentStep.id === Steps.VALUE_THE_MOST_CHOICES_1 || currentStep.id === Steps.VALUE_THE_MOST_CHOICES_2)) ||
          (scenario.type === ScenarioType.FIBER_OPTIC_ALERT &&
            currentStep &&
            currentStep.id === Steps.CONFIRM_FIBER_OPTIC_ALERT) ||
          (scenario.type === ScenarioType.FIBER_OPTIC_ALERT && currentStep && currentStep.id === Steps.EMAIL_ADDRESS) ||
          (scenario.type === ScenarioType.FIBER_OPTIC_ALERT &&
            currentStep &&
            currentStep.id === Steps.ADDRESS_LOCATION) ||
          (scenario.type === ScenarioType.FIBER_OPTIC_ALERT &&
            currentStep &&
            currentStep.id === Steps.CONFIRM_ALERT)) && <Accordions />}
    </Flex>
  )
}
